import {
  EKycUploadContentType,
  useKycGetTemporaryFolderUploadSignedUrlLazyQuery,
  useKycGetUploadSignedUrlLazyQuery,
  useKycReadCitizenCardBackMutation,
} from '@e-kyc/graphql';
import { useTranslation } from '@e-kyc/translations';
import { useDisclosure } from '@shared/hooks';
import { applyWatermark } from '@shared/utils';
import { useRef, useState } from 'react';

import { Button, CameraCapture } from '../../../../../components';
import { useEkyc } from '../../../../../providers';
import { base64ToBlob, isAlreadyCompletedError } from '../../../utils';
import { CaptureBackModal, CaptureBackOverlay, PreviewBackOverlay } from '../_components';

function CaptureBackLogic() {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Held from the moment an ALREADY_COMPLETED error arrives until the provider's status gate
  // swaps in CompletedPage, so the UI stays busy instead of rendering a verdict it never got.
  const [isAwaitingCompletedSwap, setIsAwaitingCompletedSwap] = useState(false);
  const cameraRef = useRef<{ reset: () => void; stopWebcam: () => void }>(null);

  const [getUploadSignedUrl] = useKycGetUploadSignedUrlLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [getTemporaryFolderUploadSignedUrl] = useKycGetTemporaryFolderUploadSignedUrlLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [isOpen, { open, close }] = useDisclosure(false);

  const [readCitizenCardBack] = useKycReadCitizenCardBackMutation();

  const { setCitizenCardData, setStep, goBackToPreviousStep, userId, refetchEkycStatus } =
    useEkyc();

  const { t } = useTranslation();

  const handleConfirmImage = async (base64Image: string) => {
    setIsLoading(true);
    setHasError(false);
    const imageBlob = base64ToBlob(base64Image, 'image/jpeg');

    open();

    try {
      if (!navigator.onLine) {
        throw new Error('No internet connection');
      }
      // Get temporary folder signed URL
      const { data: getTemporaryFolderUploadSignedUrlData } =
        await getTemporaryFolderUploadSignedUrl({
          variables: {
            getTemporaryFolderUploadSignedUrlInput: {
              userId,
              contentType: EKycUploadContentType.Jpeg,
            },
          },
        });

      if (!getTemporaryFolderUploadSignedUrlData?.kycGetTemporaryFolderUploadSignedUrl) {
        throw new Error('Fail to get temporary folder signed url');
      }

      const { signedUrl: tempSignedUrl, storageObject: tempImageStorageObject } =
        getTemporaryFolderUploadSignedUrlData.kycGetTemporaryFolderUploadSignedUrl;

      // Get regular (non-temporary) signed URL
      const { data: getUploadSignedUrlData } = await getUploadSignedUrl({
        variables: {
          kycGetUploadSignedUrlGetUploadSignedUrlInput: {
            contentType: EKycUploadContentType.Jpeg,
            userId,
          },
        },
      });

      if (!getUploadSignedUrlData?.kycGetUploadSignedUrl) {
        throw new Error('Fail to get watermarked image signed url');
      }

      const { signedUrl: regularSignedUrl, storageObject: watermarkedImageStorageObject } =
        getUploadSignedUrlData.kycGetUploadSignedUrl;

      if (!tempImageStorageObject.fileName) {
        throw new Error('Invalid temporary image file name');
      }

      if (!watermarkedImageStorageObject.fileName) {
        throw new Error('Invalid watermarked image file name');
      }

      // Apply watermark to the image for the regular folder
      const watermarkedImageBlob = await applyWatermark(imageBlob);

      // Upload both images in parallel
      const [uploadTempResponse, uploadRegularResponse] = await Promise.all([
        // Upload original image to temporary folder
        fetch(tempSignedUrl, {
          method: 'PUT',
          body: imageBlob,
          headers: {
            'Content-Type': 'image/jpeg',
          },
        }),

        // Upload watermarked image to regular folder
        fetch(regularSignedUrl, {
          method: 'PUT',
          body: watermarkedImageBlob,
          headers: {
            'Content-Type': 'image/jpeg',
          },
        }),
      ]);

      // Check if both uploads were successful
      if (!uploadTempResponse.ok) {
        throw new Error('Failed to upload image to temporary folder');
      }

      if (!uploadRegularResponse.ok) {
        throw new Error('Failed to upload image to regular folder');
      }

      await readCitizenCardBack({
        variables: {
          readCitizenCardInput: {
            userId,
            watermarkedCardImageFileName: watermarkedImageStorageObject.fileName,
            temporaryCardImageFileName: tempImageStorageObject.fileName,
          },
        },
        onCompleted: (data) => {
          if (data.kycReadCitizenCardBack) {
            setCitizenCardData(data.kycReadCitizenCardBack);
          }
        },
        onError: (error) => {
          console.error('Failed to read citizen card back:', error);
          if (isAlreadyCompletedError(error)) {
            // e-KYC was completed in another tab - stay busy and let the provider's status
            // gate swap in the completed page
            setIsAwaitingCompletedSwap(true);
            refetchEkycStatus()
              .catch((refetchError) => {
                console.error('Failed to refetch e-KYC status:', refetchError);
              })
              .finally(() => {
                setIsAwaitingCompletedSwap(false);
              });
            return;
          }
          setHasError(true);
          open();
        },
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const backButton = (
    <Button
      variant='text'
      className='w-24 text-white'
      onClick={() => {
        cameraRef.current?.stopWebcam();
        goBackToPreviousStep();
      }}
    >
      {t('button.back')}
    </Button>
  );

  return (
    <>
      <CameraCapture
        ref={cameraRef}
        onConfirm={handleConfirmImage}
        overlay={<CaptureBackOverlay />}
        previewOverlay={<PreviewBackOverlay />}
        isSubmitting={isLoading || isAwaitingCompletedSwap}
        backButton={backButton}
        retryButtonText={t('capture.retry')}
        confirmButtonText={t('capture.confirm')}
      />
      <CaptureBackModal
        isOpen={isOpen}
        hasError={hasError}
        isLoading={isLoading || isAwaitingCompletedSwap}
        setStep={setStep}
        resetCamera={() => {
          cameraRef.current?.reset();
        }}
        close={close}
      />
    </>
  );
}

export default CaptureBackLogic;
